/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Theme,
} from "@mui/material";
import { motion } from "framer-motion";
import { HeatDrop } from "src/store/api/generatedApi";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { useNumberSerialiser } from "hooks/serialisers/numbers";

const monoCellSx = (theme: Theme) => ({
  fontFamily: theme.typography.body1Mono.fontFamily,
});

interface Props {
  drops: HeatDrop[];
  materials: string[];
}

export const HeatDropSummaryTable = ({ drops, materials }: Props) => {
  const { t } = useTenantTranslation();
  const units = useUnitsFormatter(false);
  const { format } = useNumberSerialiser();

  const materialDropTotals = materials.map(
    (material) =>
      drops.filter(({ material_name }) => material_name === material).length
  );
  const materialMassTotals = materials.map((material) =>
    drops
      .filter(({ material_name }) => material_name === material)
      .reduce((current, drop) => current + drop.mass, 0)
  );
  const totalMass = Object.values(materialMassTotals).reduce(
    (current, mass) => current + mass,
    0
  );
  const materialPercentages = materialMassTotals.map((mass) =>
    totalMass <= 0 ? 0 : (100 * mass) / totalMass
  );

  return (
    <Stack>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2, delay: 0.1 }}
      >
        <TableContainer component={Paper} elevation={0}>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell>{""}</TableCell>
                <TableCell>{"#"}</TableCell>
                <TableCell>{units("mass")}</TableCell>
                <TableCell>{"%"}</TableCell>
              </TableRow>
              {materials.map((material, index) => (
                <TableRow key={`${material}-totals`}>
                  <TableCell>{material}</TableCell>
                  <TableCell sx={monoCellSx}>
                    {format(materialDropTotals[index]!, { decimalPlaces: 0 })}
                  </TableCell>
                  <TableCell sx={monoCellSx}>
                    {format(materialMassTotals[index]!, { decimalPlaces: 1 })}
                  </TableCell>
                  <TableCell sx={monoCellSx}>
                    {format(materialPercentages[index]!, { decimalPlaces: 1 })}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow
                sx={{
                  "& > td": {
                    borderTop: (theme) => `3px double ${theme.palette.divider}`,
                  },
                }}
              >
                <TableCell>{t("total")}</TableCell>
                <TableCell sx={monoCellSx}>
                  {format(
                    materialDropTotals.reduce((s, n) => s + n, 0),
                    { decimalPlaces: 0 }
                  )}
                </TableCell>
                <TableCell sx={monoCellSx}>
                  {format(totalMass, { decimalPlaces: 1 })}
                </TableCell>
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </motion.div>
    </Stack>
  );
};
